import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useSocket } from "./SocketContext";
import { Helper } from "@utils/index";
import CallOverlay from "@commons/chat/components/CallOverlay";

interface CallState {
  active: boolean;
  incoming: boolean;
  conversationId: number | null;
  callerName: string | null;
  callerAvatar: string | null;
  type: "video" | "audio" | null;
  status: "idle" | "calling" | "connected" | "ended" | "rejected";
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  isAudioMuted: boolean;
  isVideoOff: boolean;
  duration: number; // call duration in seconds
}

interface CallContextType {
  callState: CallState;
  startCall: (conversationId: number, type: "video" | "audio") => Promise<void>;
  acceptCall: () => Promise<void>;
  rejectCall: () => void;
  endCall: () => void;
  toggleAudio: () => void;
  toggleVideo: () => void;
}

const CallContext = createContext<CallContextType | undefined>(undefined);

export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error("useCall must be used within a CallProvider");
  }
  return context;
};

export const CallProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { socket } = useSocket();
  const [callState, setCallState] = useState<CallState>({
    active: false,
    incoming: false,
    conversationId: null,
    callerName: null,
    callerAvatar: null,
    type: null,
    status: "idle",
    localStream: null,
    remoteStream: null,
    isAudioMuted: false,
    isVideoOff: false,
    duration: 0,
  });

  const ringtoneRef = useRef<HTMLAudioElement | null>(null);
  const callingToneRef = useRef<HTMLAudioElement | null>(null);
  const peerConnection = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    // Initialize audio objects
    ringtoneRef.current = new Audio(
      "https://assets.mixkit.co/active_storage/sfx/1359/1359-preview.mp3",
    );
    ringtoneRef.current.loop = true;

    callingToneRef.current = new Audio("/zedfo-call.mp3.mpeg");
    callingToneRef.current.loop = true;

    return () => {
      if (ringtoneRef.current) ringtoneRef.current.pause();
      if (callingToneRef.current) callingToneRef.current.pause();
    };
  }, []);

  const cleanupCall = useCallback(() => {
    if (ringtoneRef.current) ringtoneRef.current.pause();
    if (callingToneRef.current) callingToneRef.current.pause();
    if (peerConnection.current) {
      peerConnection.current.close();
      peerConnection.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }
    setCallState({
      active: false,
      incoming: false,
      conversationId: null,
      callerName: null,
      callerAvatar: null,
      type: null,
      status: "idle",
      localStream: null,
      remoteStream: null,
      isAudioMuted: false,
      isVideoOff: false,
      duration: 0,
    });
  }, []);

  const initPeerConnection = useCallback(
    (conversationId: number) => {
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });

      pc.onicecandidate = (event) => {
        if (event.candidate && socket) {
          socket.emit("call:ice-candidate", {
            conversationId,
            candidate: event.candidate,
          });
        }
      };

      pc.ontrack = (event) => {
        setCallState((prev) => ({ ...prev, remoteStream: event.streams[0] }));
      };

      pc.onconnectionstatechange = () => {
        if (
          pc.connectionState === "disconnected" ||
          pc.connectionState === "failed" ||
          pc.connectionState === "closed"
        ) {
          cleanupCall();
        }
      };

      peerConnection.current = pc;
      return pc;
    },
    [socket, cleanupCall],
  );

  const startCall = useCallback(
    async (conversationId: number, type: "video" | "audio") => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: type === "video",
          audio: true,
        });

        localStreamRef.current = stream;
        setCallState((prev) => ({
          ...prev,
          active: true,
          status: "calling",
          conversationId,
          type,
          localStream: stream,
          isAudioMuted: false,
          isVideoOff: false,
        }));

        if (callingToneRef.current) {
          callingToneRef.current
            .play()
            .catch((e) => console.warn("Audio play blocked", e));
        }

        initPeerConnection(conversationId);
        stream.getTracks().forEach((track) => {
          if (peerConnection.current)
            peerConnection.current.addTrack(track, stream);
        });

        if (socket) socket.emit("call:start", { conversationId, type });
      } catch (error) {
        console.error("Failed to start call", error);
        cleanupCall();
      }
    },
    [socket, initPeerConnection, cleanupCall],
  );

  const acceptCall = useCallback(async () => {
    if (!callState.conversationId || !callState.type) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: callState.type === "video",
        audio: true,
      });

      localStreamRef.current = stream;
      setCallState((prev) => ({
        ...prev,
        status: "connected",
        localStream: stream,
        isAudioMuted: false,
        isVideoOff: false,
      }));

      if (ringtoneRef.current) ringtoneRef.current.pause();

      const pc = initPeerConnection(callState.conversationId);
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));

      if (socket)
        socket.emit("call:join", { conversationId: callState.conversationId });
    } catch (error) {
      console.error("Failed to accept call", error);
      cleanupCall();
    }
  }, [callState, socket, initPeerConnection, cleanupCall]);

  const rejectCall = useCallback(() => {
    if (callState.conversationId && socket) {
      socket.emit("call:reject", { conversationId: callState.conversationId });
    }
    cleanupCall();
  }, [callState.conversationId, socket, cleanupCall]);

  const endCall = useCallback(() => {
    if (callState.conversationId && socket) {
      socket.emit("call:end", { conversationId: callState.conversationId });
    }
    cleanupCall();
  }, [callState.conversationId, socket, cleanupCall]);

  const toggleAudio = useCallback(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setCallState((prev) => ({
          ...prev,
          isAudioMuted: !audioTrack.enabled,
        }));
      }
    }
  }, []);

  const toggleVideo = useCallback(() => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setCallState((prev) => ({ ...prev, isVideoOff: !videoTrack.enabled }));
      }
    }
  }, []);

  useEffect(() => {
    if (!socket) {
      console.log("⚠️ CallContext: Waiting for socket connection...");
      return;
    }

    console.log("🔔 CallContext: Registering socket listeners on", socket.id);

    socket.on(
      "call:incoming",
      (data: {
        conversationId: number;
        callerName: string;
        callerAvatar?: string;
        type: "video" | "audio";
      }) => {
        console.log("📞 Incoming call event received!", data);

        // UI FIX: Join the room immediately so we listen for status changes (like call:ended)
        // even before clicking "Accept"
        socket.emit("call:join", { conversationId: data.conversationId });

        if (ringtoneRef.current) {
          ringtoneRef.current
            .play()
            .catch((e) => console.warn("Audio play blocked", e));
        }
        setCallState((prev) => ({
          ...prev,
          active: true,
          incoming: true,
          status: "idle",
          conversationId: data.conversationId,
          callerName: data.callerName,
          callerAvatar: data.callerAvatar || null,
          type: data.type,
        }));
      },
    );

    socket.on("call:user_joined", (data: { userId: number }) => {
      console.log("👤 User joined the call:", data.userId);
      // We don't pause calling tone here anymore; we pause when call is "connected"
      if (
        !callState.incoming &&
        peerConnection.current &&
        callState.conversationId
      ) {
        peerConnection.current.createOffer().then((offer) => {
          peerConnection.current?.setLocalDescription(offer);
          socket.emit("call:offer", {
            conversationId: callState.conversationId,
            offer,
          });
        });
      }
    });

    socket.on(
      "call:offer",
      async (data: { offer: RTCSessionDescriptionInit }) => {
        if (ringtoneRef.current) ringtoneRef.current.pause();
        const pc =
          peerConnection.current ||
          initPeerConnection(callState.conversationId!);
        await pc.setRemoteDescription(new RTCSessionDescription(data.offer));

        if (localStreamRef.current) {
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          socket.emit("call:answer", {
            conversationId: callState.conversationId,
            answer,
          });
        }
      },
    );

    socket.on(
      "call:answer",
      async (data: { answer: RTCSessionDescriptionInit }) => {
        console.log("🤝 Call answer received");
        if (peerConnection.current) {
          await peerConnection.current.setRemoteDescription(
            new RTCSessionDescription(data.answer),
          );
          setCallState((prev) => ({ ...prev, status: "connected" }));

          // Pause calling tone now that we are connected
          if (callingToneRef.current) callingToneRef.current.pause();
        }
      },
    );

    socket.on(
      "call:ice-candidate",
      async (data: { candidate: RTCIceCandidateInit }) => {
        if (peerConnection.current) {
          try {
            await peerConnection.current.addIceCandidate(
              new RTCIceCandidate(data.candidate),
            );
          } catch (e) {
            console.error("Error adding ice candidate", e);
          }
        }
      },
    );

    socket.on("call:rejected", () => {
      cleanupCall();
    });

    socket.on("call:ended", () => {
      cleanupCall();
    });

    return () => {
      socket.off("call:incoming");
      socket.off("call:user_joined");
      socket.off("call:offer");
      socket.off("call:answer");
      socket.off("call:ice-candidate");
      socket.off("call:rejected");
      socket.off("call:ended");
    };
  }, [
    socket,
    callState.conversationId,
    callState.incoming,
    initPeerConnection,
    cleanupCall,
  ]);

  // Call duration timer
  useEffect(() => {
    let interval: any = null;
    if (callState.active && callState.status === "connected") {
      interval = setInterval(() => {
        setCallState((prev) => ({ ...prev, duration: prev.duration + 1 }));
      }, 1000);
    } else {
      if (interval) clearInterval(interval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [callState.active, callState.status]);

  return (
    <CallContext.Provider
      value={{
        callState,
        startCall,
        acceptCall,
        rejectCall,
        endCall,
        toggleAudio,
        toggleVideo,
      }}
    >
      {children}
      {callState.active && (
        <CallOverlay
          state={callState}
          onAccept={acceptCall}
          onReject={rejectCall}
          onEnd={endCall}
          onToggleAudio={toggleAudio}
          onToggleVideo={toggleVideo}
        />
      )}
    </CallContext.Provider>
  );
};
